import { createFileRoute, Link } from "@tanstack/react-router";
import { Section } from "@/components/Section";
import { Reveal } from "@/components/Reveal";
import { JsonLd } from "@/components/JsonLd";
import { FaqAccordion } from "@/components/FaqAccordion";
import { KeyTakeaways } from "@/components/KeyTakeaways";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { ReviewedBy } from "@/components/ReviewedBy";
import { AiAnswersCallout } from "@/components/AiAnswersCallout";
import { RelatedReading } from "@/components/RelatedReading";
import { ArrowUpRight, Search, MapPin, FileText, BookOpen, BarChart3, Quote, Compass } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { SITE } from "@/content/site";
import { buildSeo, breadcrumbSchema } from "@/lib/seo";
import { faqSchema } from "@/lib/schema";

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Resources", path: "/resources" },
  { name: "Online Marketing for Small Businesses", path: "/online-marketing-small-businesses" },
];

const TAKEAWAYS = [
  "Online marketing means reaching buyers through search, social, email, and AI — channels you can measure, test, and scale down as well as up.",
  "Most small businesses should start with one paid channel for fast signal and one owned channel (SEO or email) for compounding return — not six at once.",
  "A workable starting budget is 5–10% of revenue, split between ad spend and the work that produces the content, pages, and tracking.",
  "SEO is worth it for almost any small business with a website, because unlike ads it keeps producing traffic after you stop paying.",
];

const FAQS = [
  {
    q: "How do I market my small business online?",
    a: "Start with one buyer and one outcome — not 'more traffic' but something like '30 demo requests a month from ecommerce founders.' Then fix your website (speed, mobile, clear pricing, working forms), pick one paid channel for immediate signal and one owned channel like SEO or email for compounding return, and set up measurement before you spend a taka. Run 90-day cycles: ship, measure, kill what didn't work. Most programs fail from switching too early, not from picking the wrong channel. A senior SEO consultant can scope this for you on a free call.",
  },
  {
    q: "How much should I spend on online marketing?",
    a: "A common benchmark is 5–10% of gross revenue, though early-stage businesses often spend more to build momentum. For a small Bangladeshi business, a workable starting budget is ৳25,000–60,000 a month split between ads and content. Ad spend is separate from the fee that buys the strategy and execution — the two are quoted independently. The real test is not the total but whether each taka is attributable: if you can't tell which spend produced which customer, you're flying blind. Full pricing tiers are on the pricing page.",
  },
  {
    q: "What's the best online marketing strategy for a small business?",
    a: "There is no single best strategy — the right one depends on your buyer, budget, and timeline. If you need sales this month, paid search or social ads are the fastest path. If you can wait 4–6 months, SEO compounds cheapest over time. If you already have a customer list, email and SMS pay back fastest. The common thread: pick two channels (one paid, one owned), measure obsessively, and resist the urge to add a third until the first two are profitable. Spreading thin across six channels is the most common small-business marketing mistake.",
  },
  {
    q: "How do I advertise my business online?",
    a: "Online advertising breaks down into paid search (Google Ads), paid social (Meta, TikTok, LinkedIn), and display or video. For a local business, Google Ads captured to people searching your service in your city is usually the strongest first step — intent is high and the audience is self-selecting. For product businesses, Meta and TikTok feed ads reach people who aren't searching yet but match your buyer profile. Start with one platform, a small daily budget, and a single conversion goal. Kill anything with a cost-per-acquisition above your margin within 30 days.",
  },
  {
    q: "Is SEO worth it for a small business?",
    a: "For almost any small business with a website, yes. Unlike paid ads, SEO traffic keeps coming after you stop paying — a page that ranks for a commercial keyword can produce customers for years. Local SEO (Google Business Profile, citations, reviews) is especially worth it: 'local SEO for small business' is the most-searched marketing tactic in the US right now, and it directly drives map-pack calls and visits. The catch is timeline — meaningful results take 3–6 months, so SEO pairs best with a paid channel that covers the gap while it builds.",
  },
];

type RelatedResource = {
  kind: string;
  t: string;
  d: string;
  to: string;
  cta: string;
  icon: LucideIcon;
};

const RELATED_RESOURCES: RelatedResource[] = [
  {
    kind: "Service",
    t: "SEO Services",
    d: "Buyer-intent keyword research, on-page optimization, technical fixes, and authority building — engineered to compound traffic month over month.",
    to: "/services",
    cta: "See the engagement",
    icon: Search,
  },
  {
    kind: "Service",
    t: "Digital Marketing",
    d: "Full-funnel digital marketing across search, social, and email — strategy, execution, and measurement tied to revenue, not vanity metrics.",
    to: "/services/digital-marketing",
    cta: "See the engagement",
    icon: Compass,
  },
  {
    kind: "Service",
    t: "Local SEO",
    d: "Own the map pack in your city — Google Business Profile, citations, and review velocity that turn nearby searches into calls and walk-ins.",
    to: "/services/geo",
    cta: "See the engagement",
    icon: MapPin,
  },
  {
    kind: "Guide",
    t: "What Is Digital Marketing?",
    d: "A plain-English (and বাংলা) breakdown of every channel — search, social, email, paid — with real Bangladeshi examples.",
    to: "/what-is-digital-marketing",
    cta: "Read the guide",
    icon: BookOpen,
  },
  {
    kind: "Guide",
    t: "Pricing & Packages",
    d: "Transparent tier-based pricing for SEO, local SEO, and digital marketing — what each package includes and what it costs.",
    to: "/pricing",
    cta: "See pricing",
    icon: BarChart3,
  },
  {
    kind: "Case study",
    t: "DanceAmor — Adelaide dance studio",
    d: "Website and Shopify project materials for a dance academy; verified ranking and traffic outcomes are not published.",
    to: "/case-studies/danceamor-adelaide-seo-shopify",
    cta: "Read the case study",
    icon: Quote,
  },
  {
    kind: "Case study",
    t: "Local e-commerce SEO (Bangladesh)",
    d: "How a Bangladesh-based online store grew organic traffic and conversions through technical SEO, content, and local signals.",
    to: "/case-studies/ecommerce-local-bd",
    cta: "Read the case study",
    icon: Quote,
  },
  {
    kind: "Case study",
    t: "CBD keyword research",
    d: "Buyer-intent keyword research that turned a crowded niche into a focused, rankable content roadmap for a small brand.",
    to: "/case-studies/cbd-keyword-research",
    cta: "Read the case study",
    icon: FileText,
  },
];

export const Route = createFileRoute("/online-marketing-small-businesses")({
  head: () =>
    buildSeo({
      title: "Online Marketing for Small Businesses — FAQ & Beginner's Guide | Freelancer Tamal",
      description:
        "The five questions small business owners ask most about online marketing — how to start, what to spend, which strategy to pick, how to advertise, and whether SEO is worth it. Plain-English answers with real numbers.",
      path: "/online-marketing-small-businesses",
      alternates: { bn: "/bn/online-marketing-small-businesses" },
      ogEyebrow: "Guide",
      keywords:
        "online marketing for small businesses, small business marketing, digital marketing for small business, how to market a small business online, small business advertising, is SEO worth it for small business",
    }),
  component: OnlineMarketingSmallBusinessesPage,
});

function OnlineMarketingSmallBusinessesPage() {
  return (
    <>
      <Breadcrumbs items={CRUMBS} />

      <Section
        headingLevel={1}
        eyebrow="Guide"
        title={
          <>
            Online marketing for <span className="text-gradient">small businesses.</span>
          </>
        }
      >
        <p className="max-w-2xl text-lg text-muted-foreground -mt-8">
          The five questions small business owners ask most — answered in
          plain English with real numbers, no jargon, no hard sell.
        </p>
        <ReviewedBy reviewedOn="2026-09-22" />
      </Section>

      <div className="container-x">
        <KeyTakeaways items={TAKEAWAYS} />
      </div>

      <Section eyebrow="FAQ" title={<>The five questions <span className="text-gradient">owners ask most.</span></>}>
        <Reveal>
          <FaqAccordion items={FAQS} idPrefix="faq-online-marketing" />
        </Reveal>
        <JsonLd data={faqSchema(FAQS)} />
      </Section>

      <Section className="!pt-0">
        <AiAnswersCallout
          context="online marketing for small businesses"
          questionSlugs={[
            "what-is-aeo",
            "aeo-worth-it-small-business",
            "local-seo-still-matter",
            "get-cited-by-chatgpt",
          ]}
        />
      </Section>

      <Section
        eyebrow="Related resources"
        title={<>Services, guides & <span className="text-gradient">proof.</span></>}
        subtitle="The work behind the answers above — services I run for small businesses, deep-dive guides, and case studies with real numbers."
      >
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {RELATED_RESOURCES.map((r, i) => {
            const Icon = r.icon;
            return (
              <Reveal key={r.to} delay={(i % 3) * 0.06}>
                <Link
                  to={r.to as never}
                  className="group flex h-full flex-col rounded-3xl border border-border bg-card p-7 hover:border-primary/40 hover:shadow-[var(--shadow-glow)] transition"
                >
                  <div className="flex items-center justify-between">
                    <Icon className="h-6 w-6 text-primary" />
                    <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                      {r.kind}
                    </span>
                  </div>
                  <h3 className="mt-5 font-display text-xl leading-snug">{r.t}</h3>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{r.d}</p>
                  <span className="mt-auto pt-5 inline-flex items-center gap-1 text-sm font-medium text-primary">
                    {r.cta}
                    <ArrowUpRight size={15} className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition" />
                  </span>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </Section>

      <RelatedReading
        eyebrow="Related reading"
        title={<>Guides that go <span className="text-gradient">deeper.</span></>}
        description="The pricing, ROI, and strategy guides behind the answers above — free to read, no signup."
        hrefs={[
          "/what-is-digital-marketing",
          "/blog/seo-service-price-in-bangladesh-2026",
          "/blog/monthly-seo-package-bangladesh-2026",
          "/blog/how-long-does-seo-take-bangladesh-2026",
          "/blog/90-day-seo-roadmap-bangladesh-small-business-2026",
          "/blog/local-seo-cost-bangladesh-2026",
        ]}
      />

      <Section className="!pt-0">
        <div className="relative overflow-hidden rounded-[2rem] p-12 lg:p-16 text-center bg-card border border-border">
          <h2 className="font-display text-3xl md:text-4xl leading-tight max-w-2xl mx-auto">
            Want a tailored plan for <span className="text-gradient">your business?</span>
          </h2>
          <p className="mt-5 max-w-lg mx-auto text-sm text-muted-foreground">
            Tell me what you sell and where. I'll reply within one business day
            with an honest take on which channels are worth it for you — no
            pressure, no generic pitch.
          </p>
          <Link
            to="/contact"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-7 py-4 text-sm font-medium hover:shadow-[var(--shadow-glow)] transition"
          >
            Book a strategy call <ArrowUpRight size={16} />
          </Link>
        </div>
      </Section>

      <JsonLd data={breadcrumbSchema(CRUMBS)} />
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline:
            "Online Marketing for Small Businesses — FAQ & Beginner's Guide",
          description:
            "The five questions small business owners ask most about online marketing, answered in plain English with real numbers.",
          author: {
            "@type": "Person",
            "@id": `${SITE.url}/#person`,
            name: SITE.fullName,
            url: `${SITE.url}/about`,
          },
          publisher: {
            "@type": "Person",
            "@id": `${SITE.url}/#person`,
            name: SITE.fullName,
          },
          url: `${SITE.url}/online-marketing-small-businesses`,
        }}
      />
    </>
  );
}
